// The viewer's DISPLAY zone, resolved once per page load.
//
// The zone model has two separate chains and conflating them is the classic bug
// (see shared/src/time.ts). This module only ever answers the DISPLAY question:
// what zone should times be shown IN.
//
//   account's saved time_zone -> the browser's zone
//
// The owning group is deliberately NOT in this chain. A group is where an event
// was authored, not where the reader is sitting, and using it as a display
// fallback would show a Denver member their Chicago group's events in Chicago
// time.
//
// The server's own fallback is the SITE zone, which the SPA has no way to know.
// The browser zone stands in for it here: for a signed-in user with a saved zone
// the two chains agree exactly, and for anyone else the browser's guess is a
// better answer than a hard-coded default would be.

import { fetchMe } from './api/client.js';

let cached: string | null = null;

/** The browser's own zone. Always available, never empty. */
function browserZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

/**
 * The viewer's display zone. Resolves the account zone on first call and reuses
 * it thereafter, so a list of thirty events does not make thirty /me requests.
 */
export async function viewerZone(): Promise<string> {
  if (cached !== null) return cached;
  try {
    const me = await fetchMe();
    cached = me.timeZone || browserZone();
  } catch {
    // Not signed in, or /me failed. The browser zone is still a usable answer,
    // and a timezone lookup must never be the thing that blanks a page.
    cached = browserZone();
  }
  return cached;
}

/** Whatever is already known, without awaiting. Falls back to the browser zone. */
export function viewerZoneSync(): string {
  return cached ?? browserZone();
}

/** Drop the cache after the user changes their zone in settings. */
export function forgetViewerZone(): void {
  cached = null;
}
